// src/forms/data_hub_cors_user_data.js
// Extracted from data_hub.js — handles CORS iframe user data response merging.

import { white_list_outgoing_person_fields } from '../helpers/static_vars';
import { apply_server_forgotten_state } from '../analytics/privacy';
import { validate_and_clean_handlebars_from_user_data } from '../helpers/handlebars_validation';
import { re_prefill_all_forms } from './shared_form_ux_functions';
import { updateActblueConfigWithDatahub } from '../helpers/actblue_integration';
import { merge_user_data_browser } from '../helpers/user_data_smart_merge';
import { getURLUserData } from '../helpers/get_process_user_data';
import { apply_explicit_user_data_overrides } from '../helpers/user_data_cache_sync';

/**
 * Processes CORS iframe user data response (the LOAD direction).
 * Whitelist-filters, validates handlebars, smart-merges with merge_user_data_browser, then
 * re-applies URL params + the data-hub-initial-user-data seed on top so the late payload
 * can't clobber explicit intent. Updates user_data, re-prefills forms, and dispatches
 * data-hub-new-user-data — the save listener handles persisting to parent LS + the
 * action-origin LS mirror (incl. the JWT); this function no longer writes caches inline.
 *
 * @param {Object} data_hub_instance - The data_hub class instance.
 * @param {Object} payload - Whitelisted user data from the CORS iframe.
 */
export async function got_cors_user_data_cache(data_hub_instance, payload) {
	if (!payload) return;

	if (typeof payload !== 'object' || Array.isArray(payload)) {
		return;
	}

	Object.keys(payload).forEach((key) => {
		if (!white_list_outgoing_person_fields.includes(key)) {
			delete payload[key];
		}
	});

	// CCPA: a forgotten (erased/suppressed) person quick-exits analytics and
	// sets the persistent org-scoped DNT flag. Merge/prefill still proceeds —
	// suppression gates tracking, not form UX.
	apply_server_forgotten_state(data_hub_instance, payload);

	// Defense in depth: don't run the merge/prefill/event pipeline when the
	// server (or caller) gave us nothing to merge. Otherwise we'd dispatch a
	// data-hub-new-user-data event and re-prefill forms with unchanged data.
	if (Object.keys(payload).length === 0) {
		if (typeof data_hub_instance.resolve_user_data_loaded === 'function') {
			data_hub_instance.resolve_user_data_loaded();
		}
		return;
	}

	let newMergedUserData;
	try {
		const cleaned_payload = validate_and_clean_handlebars_from_user_data(
			payload,
			white_list_outgoing_person_fields,
			data_hub_instance.error_function
		);

		const base_for_merge = {
			...(window?.data_hub_user_data || {}),
			...(data_hub_instance.user_data || {}),
		};

		newMergedUserData = await merge_user_data_browser(base_for_merge, cleaned_payload || {});
		// Re-apply the highest-priority explicit sources on top so this late server/iframe
		// payload can't clobber URL params or the data-hub-initial-user-data seed. URL params
		// are recomputed from the already-cached url_params; config_user_data is read off the
		// instance. Keeps the async CORS-return ordering identical to the sync init cascade.
		const url_param_user_data = await getURLUserData(data_hub_instance.url_params, data_hub_instance);
		newMergedUserData = await apply_explicit_user_data_overrides(
			data_hub_instance,
			newMergedUserData,
			url_param_user_data
		);
		data_hub_instance.user_data = newMergedUserData;
		window.data_hub_user_data = newMergedUserData;
	} catch (error) {
		data_hub_instance.error_function?.(`Error validating CORS payload for handlebars: ${error}`, null);
		const fallback_base = {
			...(window?.data_hub_user_data || {}),
			...(data_hub_instance.user_data || {}),
		};
		newMergedUserData = await merge_user_data_browser(fallback_base, payload);
		const url_param_user_data = await getURLUserData(data_hub_instance.url_params, data_hub_instance);
		newMergedUserData = await apply_explicit_user_data_overrides(
			data_hub_instance,
			newMergedUserData,
			url_param_user_data
		);
		data_hub_instance.user_data = newMergedUserData;
		window.data_hub_user_data = newMergedUserData;
	}

	// Cache writes (parent LS + action-origin LS mirror, plus the JWT) are handled by the
	// SAVE-direction listener reacting to the data-hub-new-user-data dispatch below — see
	// helpers/user_data_cache_sync.js. Routing through that single-flight is what keeps two
	// code paths from racing the same localStorage slot.

	if (typeof data_hub_instance.resolve_user_data_loaded === 'function') {
		data_hub_instance.resolve_user_data_loaded();
	}

	document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
		detail: {
			user_data: data_hub_instance.user_data || {},
			jwt_cookie: data_hub_instance.jwt_cookie || null,
			prefill_input_selector: data_hub_instance.prefill_input_selector || null,
			data_hub_instance: data_hub_instance,
			target: data_hub_instance.target || document,
			source: 'cors_iframe'
		},
		bubbles: true,
		composed: true
	}));

	re_prefill_all_forms(newMergedUserData, data_hub_instance.prefill_input_selector, data_hub_instance);

	updateActblueConfigWithDatahub(data_hub_instance);
}
